<template>
	<div
		class="g-block-pay-file-unlock"
		:class="{
			'g-block-pay-file-unlock--edit': editable,
			'is-active': editable && active,
			'g-block-pay-file-unlock--h5': h5,
			'g-block-pay-file-unlock--compact': compact,
			'g-block-pay-file-unlock--unlocked': isUnlocked,
		}"
		:style="{ '--pay-file-unlock-radius': `${fileData.borderRadius}px` }"
		@click="onWrapClick"
	>
		<div class="g-block-pay-file-unlock__card" :style="cardStyle">
			<div class="g-block-pay-file-unlock__header">
				<div class="g-block-pay-file-unlock__file-icon" aria-hidden="true">
					<img
						class="g-block-pay-file-unlock__file-icon-img"
						:src="fileIconUrl"
						:alt="fileIconMark"
					/>
				</div>
				<div class="g-block-pay-file-unlock__file-info">
					<div class="g-block-pay-file-unlock__file-name">{{ displayFileName }}</div>
					<div v-if="metaText" class="g-block-pay-file-unlock__file-meta">{{ metaText }}</div>
				</div>
			</div>

			<div v-if="previewCards.length" class="g-block-pay-file-unlock__preview-box">
				<span class="g-block-pay-file-unlock__preview-badge" v-if="!isUnlocked">免费试看</span>
				<div class="g-block-pay-file-unlock__preview-row">
					<div
						v-for="card in previewCards"
						:key="card.key"
						@click.stop="onPreviewClick(card, $event)"
						class="g-block-pay-file-unlock__preview-card"
						:class="{ 'is-locked': card.locked }"
					>
						<div
							class="g-block-pay-file-unlock__preview-body"
							:class="{
								'is-locked': card.locked,
								'is-previewable': isCardPreviewable(card),
							}"
							@click="onPreviewClick(card, $event)"
						>
							<span
								class="g-block-pay-file-unlock__preview-index"
								:class="{ 'is-locked': card.locked }"
							>{{ formatPageIndex(card.index) }}</span>
							<template v-if="!card.locked">
								<img
									v-if="getPreviewImageUrl(card.image)"
									class="g-block-pay-file-unlock__preview-image"
									:src="getPreviewImageUrl(card.image)"
									alt=""
								/>
								<div v-else class="g-block-pay-file-unlock__preview-placeholder">暂无预览</div>
							</template>
							<template v-else>
								<img
									v-if="getPreviewImageUrl(card.image)"
									class="g-block-pay-file-unlock__preview-bg"
									:src="getPreviewImageUrl(card.image)"
									alt=""
								/>
								<div class="g-block-pay-file-unlock__locked-mask">
									<i class="mbm-iconfont mbm-suoding g-block-pay-file-unlock__locked-icon" aria-hidden="true"></i>
								</div>
							</template>
						</div>
					</div>
				</div>
				<p v-if="showPreviewDesc" class="g-block-pay-file-unlock__preview-desc">
					解锁后可查看全部
					<strong>{{ pageCount }}</strong>
					页内容
				</p>
			</div>

			<div v-if="!isUnlocked && !shouldHidePrice" class="g-block-pay-file-unlock__pay">
				<div class="g-block-pay-file-unlock__price">
					<span class="g-block-pay-file-unlock__currency">{{ fileData.currency }}</span>
					<span class="g-block-pay-file-unlock__price-value">{{ fileData.price }}</span>
				</div>
				<button
					type="button"
					class="g-block-pay-file-unlock__unlock-btn"
					:style="buttonStyle"
					@click.stop="onUnlockClick"
				>
					<i class="mbm-iconfont mbm-suoding g-block-pay-file-unlock__unlock-icon"></i>
					<span>{{ fileData.buttonText }}</span>
				</button>
				<div class="g-block-pay-file-unlock__trust">
					<i class="mbm-iconfont mbm-check g-block-pay-file-unlock__trust-icon"></i>
					<span>{{ fileData.trustText }}</span>
				</div>
			</div>

			<div class="g-block-pay-file-unlock__footer-features">
				<div
					v-for="feature in fileData.footerFeatures"
					:key="feature.id"
					class="g-block-pay-file-unlock__footer-feature"
				>
					<div
						class="g-block-pay-file-unlock__footer-feature-icon"
						:class="featureIconClass(feature.icon)"
					>
						<i :class="featureIconName(feature.icon)"></i>
					</div>
					<div class="g-block-pay-file-unlock__footer-feature-title">{{ feature.title }}</div>
					<div class="g-block-pay-file-unlock__footer-feature-sub">{{ feature.subtitle }}</div>
				</div>
			</div>

			<div class="g-block-pay-file-unlock__footer-security">
				<i class="mbm-iconfont mbm-anquan"></i>
				<span>{{ fileData.footerText }}</span>
			</div>
		</div>

		<ImageGalleryLightbox
			v-model:visible="lightboxVisible"
			:urls="lightboxUrls"
			:initial-index="lightboxIndex"
		/>
	</div>
</template>

<script>
	import { LOCAL_ASSET } from '../../../utils/localAssets.js';
	import { geditPayUnlockMixin } from '../../../mixins/geditPayUnlock.js';
	import ImageGalleryLightbox from '../../../components/ImageGalleryLightbox.vue';
	import {
		getPayFileUnlockCardStyle,
		getPayFileUnlockFileIconKey,
		getPayFileUnlockFileIconMark,
		getPayFileUnlockGalleryUrls,
		getPayFileUnlockMetaText,
		getPayFileUnlockPreviewCards,
		getPayFileUnlockPreviewImageUrl,
		isPayFileUnlockUnlocked,
		normalizePayFileUnlockData,
		setPayFileUnlockUnlocked,
	} from '../../../utils/payFileUnlock.js';

	export default {
		name: 'GPayFileUnlockShell',
		mixins: [geditPayUnlockMixin],
		components: { ImageGalleryLightbox },
		props: {
			data: {
				type: Object,
				required: true,
			},
			blockId: {
				type: String,
				default: '',
			},
			editable: {
				type: Boolean,
				default: false,
			},
			active: {
				type: Boolean,
				default: false,
			},
			h5: {
				type: Boolean,
				default: false,
			},
			compact: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['select'],
		data() {
			return {
				unlockedLocal: false,
				lightboxVisible: false,
				lightboxIndex: 0,
			};
		},
		computed: {
			fileData() {
				return normalizePayFileUnlockData(this.data);
			},
			cardStyle() {
				return getPayFileUnlockCardStyle(this.fileData, { h5: this.h5 });
			},
			buttonStyle() {
				return {
					background: this.fileData.buttonBg,
				};
			},
			displayFileName() {
				return this.fileData.fileName || '未上传文档';
			},
			metaText() {
				return getPayFileUnlockMetaText(this.fileData);
			},
			fileIconMark() {
				return getPayFileUnlockFileIconMark(this.fileData.fileName);
			},
			fileIconUrl() {
				const key = getPayFileUnlockFileIconKey(this.fileData.fileName);
				return LOCAL_ASSET.payFileIcons[key] || LOCAL_ASSET.payFileIcons.file;
			},
			fileIconKey() {
				return getPayFileUnlockFileIconKey(this.fileData.fileName);
			},
			fileIconUrl() {
				return LOCAL_ASSET.payFileIcons[this.fileIconKey] || LOCAL_ASSET.payFileIcons.file;
			},
			pageCount() {
				return this.fileData.items.length;
			},
			isUnlocked() {
				if (this.editable) return false;
				return this.unlockedLocal || isPayFileUnlockUnlocked(this.blockId) || this.isGeditModulePaid;
			},
			previewCards() {
				if (this.isUnlocked) {
					return this.fileData.items.map((item, index) => ({
						key: item.id,
						index,
						image: item.image,
						locked: false,
					}));
				}
				return getPayFileUnlockPreviewCards(this.fileData);
			},
			showPreviewDesc() {
				return !this.isUnlocked
					&& this.pageCount > 0
					&& this.fileData.freePreviewCount > 0
					&& this.fileData.freePreviewCount < this.pageCount;
			},
			lightboxUrls() {
				const urls = getPayFileUnlockGalleryUrls(this.fileData);
				if (this.isUnlocked) {
					return urls;
				}
				return urls.slice(0, this.fileData.freePreviewCount);
			},
		},
		watch: {
			blockId: {
				immediate: true,
				handler() {
					this.syncUnlockState();
				},
			},
		},
		methods: {
			syncUnlockState() {
				this.unlockedLocal = isPayFileUnlockUnlocked(this.blockId);
			},
			getPreviewImageUrl(image) {
				return getPayFileUnlockPreviewImageUrl(image);
			},
			formatPageIndex(index) {
				return String(index + 1).padStart(2, '0');
			},
			featureIconClass(icon) {
				const map = {
					shield: 'is-shield',
					download: 'is-download',
					valid: 'is-valid',
				};
				return map[icon] || map.shield;
			},
			featureIconName(icon) {
				const map = {
					shield: 'mbm-iconfont mbm-anquan',
					download: 'mbm-iconfont mbm-xiazai1',
					valid: 'mbm-iconfont mbm-check',
				};
				return map[icon] || map.shield;
			},
		onWrapClick() {
			if (this.editable) {
				this.$emit('select');
			}
		},
			onUnlockClick() {
				this.runGeditUnlockFlow({
					applyUnlock: () => {
						setPayFileUnlockUnlocked(this.blockId, true);
						this.unlockedLocal = true;
					},
				});
			},
			isCardPreviewable(card) {
				if (!this.h5 || this.editable ) return false;
				return Boolean(this.getPreviewImageUrl(card.image));
			},
			onPreviewClick(card, event) {
				if(card.locked){
					this.onUnlockClick();
					return
				}
				if (!this.isCardPreviewable(card)) return;
				event.stopPropagation();
				this.lightboxIndex = card.index;
				this.lightboxVisible = true;
			},
		},
	};
</script>

<style scoped lang="less">
	.g-block-pay-file-unlock {
		box-sizing: border-box;

		&--edit {
			border: 1px solid transparent;
			border-radius: 4px;
			transition: border-color 0.2s, box-shadow 0.2s;
			cursor: pointer;

			&.is-active {
				border-color: #285af9;
				box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15);
			}
		}
	}

	.g-block-pay-file-unlock__card {
		background: #fff;
		overflow: hidden;
		box-shadow: 0 0 12px rgba(0, 0, 0, 0.1);
	}

	.g-block-pay-file-unlock__header {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 16px 10px 12px;
	}

	.g-block-pay-file-unlock__file-icon {
		flex-shrink: 0;
		width: 56px;
		height: 56px;
		border-radius: 8px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.g-block-pay-file-unlock__file-icon-img {
		width: 56px;
		height: 56px;
		object-fit: contain;
	}

	.g-block-pay-file-unlock__file-icon-mark {
		display: none;
	}

	.g-block-pay-file-unlock__file-info {
		flex: 1;
		min-width: 0;
	}

	.g-block-pay-file-unlock__file-name {
		font-size: 17px;
		font-weight: 700;
		color: #1a1a1a;
		line-height: 1.35;
		word-break: break-all;
	}

	.g-block-pay-file-unlock__file-meta {
		margin-top: 4px;
		font-size: 12px;
		color: #999;
		line-height: 1.4;
	}

	.g-block-pay-file-unlock__preview-box {
		position: relative;
		margin: 0 10px;
		padding: 14px 0 14px;
		border-radius: 12px;
	}

	.g-block-pay-file-unlock__preview-badge {
		padding: 2px 8px;
		margin-bottom: 10px;
		border-radius: 4px;
		background: #285af9;
		color: #fff;
		font-size: 11px;
		line-height: 1.4;
		display: inline-block;
	}

	.g-block-pay-file-unlock__preview-row {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 8px;
	}

	.g-block-pay-file-unlock__preview-card {
		min-height: 0;
		padding: 0;
		background: #fff;
		box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
		box-sizing: border-box;
		overflow: hidden;
		border-radius: var(--pay-file-unlock-radius, 8px);
	}

	.g-block-pay-file-unlock__preview-body {
		position: relative;
		width: 100%;
		aspect-ratio: 3 / 4;
		padding: 0;
		box-sizing: border-box;
		overflow: hidden;

		&.is-previewable {
			cursor: pointer;
		}
	}

	.g-block-pay-file-unlock__preview-index {
		position: absolute;
		top: 6px;
		right: 6px;
		z-index: 2;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 22px;
		height: 18px;
		padding: 0 5px;
		border-radius: 4px;
		background: #285af9;
		color: #fff;
		font-size: 10px;
		font-weight: 600;
		line-height: 1;
		box-sizing: border-box;

		&.is-locked {
			background: rgba(0, 0, 0, 0.28);
		}
	}

	.g-block-pay-file-unlock__preview-image,
	.g-block-pay-file-unlock__preview-bg {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.g-block-pay-file-unlock__preview-bg {
		position: absolute;
		inset: 0;
		filter: blur(10px);
		transform: scale(1.06);
	}

	.g-block-pay-file-unlock__preview-placeholder {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		background: #f0f2f5;
		font-size: 10px;
		color: #bbb;
	}

	.g-block-pay-file-unlock__locked-mask {
		position: absolute;
		inset: 0;
		z-index: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 4px;
		background: rgba(0, 0, 0, 0.15);
	}

	.g-block-pay-file-unlock__locked-icon {
		font-size: 22px;
		color: #fff;
	}

	.g-block-pay-file-unlock--compact {
		.g-block-pay-file-unlock__locked-icon {
			font-size: 20px;
		}
	}

	.g-block-pay-file-unlock__locked-title {
		font-size: 10px;
		color: #fff;
		line-height: 1.3;
	}

	.g-block-pay-file-unlock__locked-range {
		font-size: 9px;
		color: rgba(255, 255, 255, 0.85);
		line-height: 1.3;
	}

	.g-block-pay-file-unlock__preview-desc {
		margin: 12px 0 0;
		font-size: 12px;
		color: #999;
		text-align: center;
		line-height: 1.5;

		strong {
			color: #285af9;
			font-weight: 600;
		}
	}

	.g-block-pay-file-unlock__pay {
		padding: 16px 16px 0;
	}

	.g-block-pay-file-unlock__price {
		display: flex;
		align-items: baseline;
		justify-content: center;
		color: #ff4d4f;
		line-height: 1;
	}

	.g-block-pay-file-unlock__currency {
		font-size: 16px;
		font-weight: 600;
		margin-right: 2px;
	}

	.g-block-pay-file-unlock__price-value {
		font-size: 32px;
		font-weight: 700;
	}

	.g-block-pay-file-unlock__unlock-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		width: 100%;
		margin-top: 12px;
		border: none;
		border-radius: 999px;
		padding: 12px 16px;
		font-size: 15px;
		font-weight: 600;
		color: #fff;
		cursor: pointer;
		line-height: 1.2;

		&:active {
			opacity: 0.92;
		}
	}

	.g-block-pay-file-unlock__unlock-icon {
		font-size: 17px;
		flex-shrink: 0;
		color: #fff;
	}

	.g-block-pay-file-unlock__trust {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 4px;
		margin-top: 10px;
		font-size: 12px;
		color: #52c41a;
		line-height: 1.4;
	}

	.g-block-pay-file-unlock__trust-icon {
		font-size: 12px;
	}

	.g-block-pay-file-unlock__footer-features {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 8px;
		margin: 16px 8px 0;
		padding: 14px 8px;
		background: #f7f8fa;
		border-radius: 12px;
	}

	.g-block-pay-file-unlock__footer-feature {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
	}

	.g-block-pay-file-unlock__footer-feature-icon {
		width: 34px;
		height: 34px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 16px;

		&.is-shield {
			background: #eef4ff;
			color: #285af9;
		}

		&.is-download {
			background: #eef4ff;
			color: #285af9;
		}

		&.is-valid {
			background: #fff7e6;
			color: #faad14;
		}
	}

	.g-block-pay-file-unlock__footer-feature-title {
		margin-top: 8px;
		font-size: 12px;
		font-weight: 600;
		color: #333;
		line-height: 1.3;
	}

	.g-block-pay-file-unlock__footer-feature-sub {
		margin-top: 2px;
		font-size: 10px;
		color: #999;
		line-height: 1.3;
	}

	.g-block-pay-file-unlock__footer-security {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 4px;
		padding: 12px 16px 16px;
		font-size: 11px;
		color: #bbb;
		line-height: 1.4;

		.mbm-anquan {
			font-size: 12px;
			color: #52c41a;
		}
	}

	.g-block-pay-file-unlock--h5 {
		width: 100%;

		.g-block-pay-file-unlock__card {
			box-shadow: 0 0 0.24rem rgba(0, 0, 0, 0.1);
		}

		.g-block-pay-file-unlock__header {
			gap: 0.28rem;
			padding: 0.36rem 0.2rem 0.28rem;
		}

		.g-block-pay-file-unlock__file-icon {
			width: 1.24rem;
			height: 1.24rem;
			border-radius: 0.2rem;
		}

		.g-block-pay-file-unlock__file-icon-img {
			width: 1.12rem;
			height: 1.12rem;
		}

		.g-block-pay-file-unlock__file-icon-mark {
			font-size: 0.48rem;
		}

		.g-block-pay-file-unlock__file-name {
			font-size: 0.36rem;
		}

		.g-block-pay-file-unlock__file-meta {
			margin-top: 0.12rem;
			font-size: 0.26rem;
		}

		.g-block-pay-file-unlock__preview-box {
			margin: 0 0.2rem;
			padding: 0.28rem 0 0.28rem;
		}

		.g-block-pay-file-unlock__preview-badge {
			margin-bottom: 0.2rem;
			padding: 0.04rem 0.16rem;
			border-radius: 0.08rem;
			font-size: 0.22rem;
		}

		.g-block-pay-file-unlock__preview-row {
			gap: 0.16rem;
		}

		.g-block-pay-file-unlock__preview-card {
			border-radius: 0.16rem;
			box-shadow: 0 0.02rem 0.08rem rgba(0, 0, 0, 0.06);
		}

		.g-block-pay-file-unlock__preview-index {
			top: 0.12rem;
			right: 0.12rem;
			min-width: 0.4rem;
			height: 0.36rem;
			padding: 0 0.1rem;
			border-radius: 0.08rem;
			font-size: 0.22rem;
		}

		.g-block-pay-file-unlock__preview-placeholder {
			font-size: 0.2rem;
		}

		.g-block-pay-file-unlock__locked-mask {
			gap: 0.08rem;
		}

		.g-block-pay-file-unlock__locked-icon {
			font-size: 0.4rem;
		}

		.g-block-pay-file-unlock__locked-title {
			font-size: 0.22rem;
		}

		.g-block-pay-file-unlock__locked-range {
			font-size: 0.2rem;
		}

		.g-block-pay-file-unlock__preview-desc {
			margin-top: 0.24rem;
			font-size: 0.24rem;
		}

		.g-block-pay-file-unlock__pay {
			padding: 0.32rem 0.32rem 0;
		}

		.g-block-pay-file-unlock__currency {
			font-size: 0.32rem;
		}

		.g-block-pay-file-unlock__price-value {
			font-size: 0.64rem;
		}

		.g-block-pay-file-unlock__unlock-btn {
			gap: 0.12rem;
			margin-top: 0.24rem;
			padding: 0.24rem 0.32rem;
			font-size: 0.3rem;
		}

		.g-block-pay-file-unlock__unlock-icon {
			font-size: 0.34rem;
		}

		.g-block-pay-file-unlock__trust {
			gap: 0.08rem;
			margin-top: 0.2rem;
			font-size: 0.24rem;
		}

		.g-block-pay-file-unlock__trust-icon {
			font-size: 0.24rem;
		}

		.g-block-pay-file-unlock__footer-features {
			gap: 0.16rem;
			margin: 0.32rem 0.2rem 0;
			padding: 0.28rem 0.16rem;
			border-radius: 0.24rem;
		}

		.g-block-pay-file-unlock__footer-feature-icon {
			width: 0.68rem;
			height: 0.68rem;
			font-size: 0.32rem;
		}

		.g-block-pay-file-unlock__footer-feature-title {
			margin-top: 0.16rem;
			font-size: 0.24rem;
		}

		.g-block-pay-file-unlock__footer-feature-sub {
			margin-top: 0.04rem;
			font-size: 0.2rem;
		}

		.g-block-pay-file-unlock__footer-security {
			gap: 0.08rem;
			padding: 0.24rem 0.32rem 0.32rem;
			font-size: 0.22rem;

			.mbm-anquan {
				font-size: 0.24rem;
			}
		}
	}
</style>
